import React, { useState } from "react";
import { createPortal } from "react-dom";
import { CalendarDays, Mail, Phone, UserRound, X } from "lucide-react";
import useModalDismiss from "../../common/useModalDismiss";
import { formatDate, formatDateTime, formatMoney, formatPhone, initials, telHref } from "../reportsFormat";

const StatusChip = ({ status, t }) => (
	<span className={`sf-rap-chip is-${status}`}>{t.status[status] || t.status.unknown}</span>
);

const TABS = ["summary", "history"];

/* One client's record.

   Two tabs rather than one long scroll, matching the exclusion, base schedule
   and service-category sheets: the stats and the booking list are separate
   questions, and on a phone a regular with sixty visits pushed every figure
   above the fold out of reach. The tab strip is the same segmented pill those
   modals use — see .sf-rap-modal-tabs.

   Read-only, so a backdrop click is safe to dismiss on — there is nothing typed
   here to lose. The hook also stops a drag that starts inside the panel and
   releases on the overlay from reading as a backdrop click. */
const CustomerDetailModal = ({ detail, loading, error, onClose, t, language }) => {
	const dismissProps = useModalDismiss(onClose, { dismissOnBackdrop: true });
	const [tab, setTab] = useState("summary");

	const customer = detail?.customer;
	const currency = detail?.currency;
	const history = detail?.history || [];

	const displayName = customer
		? customer.isAnonymousBucket
			? t.customers.anonymousBucket
			: customer.name || customer.email || t.customers.noName
		: "";

	const phone = customer?.phone ? formatPhone(customer.phone) : null;
	const tel = customer?.phone ? telHref(customer.phone) : null;
	const hasContact = Boolean(customer?.email || phone);

	return createPortal(
		<div className="sf-rap-modal-backdrop" {...dismissProps}>
			<div className="sf-rap-modal sf-rap-modal--detail" role="dialog" aria-modal="true" aria-label={t.detail.title}>
				<header className="sf-rap-modal-head">
					<div className="sf-rap-modal-head-copy">
						<h2>{t.detail.title}</h2>
					</div>
					<button type="button" className="sf-rap-icon-btn" onClick={onClose} aria-label={t.detail.close}>
						<X size={18} />
					</button>
				</header>

				{loading && (
					<div className="sf-rap-modal-body">
						<div className="sf-rap-loading">
							<span className="sf-rap-spinner" />
						</div>
					</div>
				)}

				{!loading && error && (
					<div className="sf-rap-modal-body">
						<p className="sf-rap-error">{error}</p>
					</div>
				)}

				{!loading && !error && customer && (
					<>
						{/* Identity sits above the tabs, not inside one: whichever
						    tab is open, it has to be obvious whose record this is. */}
						<div className="sf-rap-modal-person">
							<span
								className={`sf-rap-avatar sf-rap-avatar--lg ${customer.isAnonymousBucket ? "is-anon" : ""}`}
							>
								{customer.isAnonymousBucket ? "?" : initials(customer.name || customer.email)}
							</span>
							<span className="sf-rap-person-text">
								<strong>{displayName}</strong>
								<span className={`sf-rap-tag ${customer.isRegistered ? "is-registered" : ""}`}>
									{customer.isRegistered ? t.customers.registered : t.customers.guest}
								</span>
							</span>
						</div>

						<nav className="sf-rap-modal-tabs" role="tablist">
							{TABS.map((key) => (
								<button
									key={key}
									type="button"
									role="tab"
									aria-selected={tab === key}
									className={`sf-rap-modal-tab ${tab === key ? "is-active" : ""}`}
									onClick={() => setTab(key)}
								>
									{t.detail.tabs[key]}
									{key === "history" && history.length > 0 && (
										<span className="sf-rap-modal-tab-count">{history.length}</span>
									)}
								</button>
							))}
						</nav>

						<div className="sf-rap-modal-body">
							{tab === "summary" && (
								<>
									<h3 className="sf-rap-modal-h3">
										<UserRound size={16} />
										{t.detail.contact}
									</h3>

									{hasContact ? (
										<div className="sf-rap-contact">
											{customer.email && (
												<a href={`mailto:${customer.email}`} className="sf-rap-contact-item">
													<span className="sf-rap-contact-icon">
														<Mail size={14} />
													</span>
													<span className="sf-rap-contact-text">
														<em>{t.detail.email}</em>
														{/* Long addresses must wrap inside the pill rather
														    than widen it past the modal. */}
														<strong>{customer.email}</strong>
													</span>
												</a>
											)}
											{phone && (
												<a href={tel ? `tel:${tel}` : undefined} className="sf-rap-contact-item">
													<span className="sf-rap-contact-icon">
														<Phone size={14} />
													</span>
													<span className="sf-rap-contact-text">
														<em>{t.detail.phone}</em>
														{/* Grouped, not the raw run of digits the booking
														    stored — this is a number staff read back down
														    a phone line. tel: still gets the bare digits. */}
														<strong className="sf-rap-contact-phone">{phone}</strong>
													</span>
												</a>
											)}
										</div>
									) : (
										<p className="sf-rap-empty">{t.detail.noContact}</p>
									)}

									<dl className="sf-rap-stats">
										<div className="is-primary">
											<dt>{t.detail.spent}</dt>
											<dd>{formatMoney(customer.lifetimeValue, currency)}</dd>
										</div>
										<div>
											<dt>{t.detail.visits}</dt>
											<dd>{customer.totalBookings}</dd>
										</div>
										<div>
											<dt>{t.detail.completed}</dt>
											<dd>{customer.completed}</dd>
										</div>
										<div>
											<dt>{t.detail.cancelled}</dt>
											<dd>{customer.cancelled}</dd>
										</div>
										<div>
											<dt>{t.detail.firstVisit}</dt>
											<dd>{formatDate(customer.firstVisit, language)}</dd>
										</div>
										<div>
											<dt>{t.detail.lastVisit}</dt>
											<dd>{formatDate(customer.lastVisit, language)}</dd>
										</div>
										{customer.favoriteService && (
											<div>
												<dt>{t.detail.favoriteService}</dt>
												<dd>{customer.favoriteService}</dd>
											</div>
										)}
										{customer.favoriteStaff && (
											<div>
												<dt>{t.detail.favoriteStaff}</dt>
												<dd>{customer.favoriteStaff}</dd>
											</div>
										)}
									</dl>
								</>
							)}

							{tab === "history" && (
								<>
									<h3 className="sf-rap-modal-h3">
										<CalendarDays size={16} />
										{t.detail.history}
									</h3>

									{history.length === 0 ? (
										<p className="sf-rap-empty">{t.detail.noHistory}</p>
									) : (
										<ul className="sf-rap-history">
											{history.map((b) => (
												<li key={b.bookingId}>
													<div className="sf-rap-history-main">
														<strong>
															{b.services
																.map(
																	(s) =>
																		(language === "es" ? s.serviceNameEs : s.serviceNameEn) ||
																		s.serviceNameEs ||
																		s.serviceNameEn,
																)
																.filter(Boolean)
																.join(", ") || "—"}
														</strong>
														<span>{formatDateTime(b.startTime, language)}</span>
													</div>
													<div className="sf-rap-history-meta">
														<StatusChip status={b.status} t={t} />
														{b.staffName && <span className="sf-rap-history-staff">{b.staffName}</span>}
														<span
															className="sf-rap-history-amount"
															/* Catalog-priced rows are estimates, so they say so on hover rather
															   than sitting beside recorded amounts unmarked. */
															title={b.amountBasis === "catalog" ? t.detail.estimatedFlag : undefined}
														>
															{formatMoney(b.amount, currency)}
															{b.amountBasis === "catalog" && <em className="sf-rap-est">~</em>}
														</span>
													</div>
												</li>
											))}
										</ul>
									)}
								</>
							)}
						</div>
					</>
				)}
			</div>
		</div>,
		document.body,
	);
};

export default CustomerDetailModal;
